import * as React from 'react'
import {
  toPrecision,
  NumericSeparators,
  getLocaleNumericSeparators,
  parseNumberFromPartialString,
  isDigit,
} from 'utils/number-util'

/**
 * Configuration for the Numeric Input hook.
 */
export interface NumericInputHookConfig {
  /**
   * The numeric value.
   */
  value?: number | null | undefined
  /**
   * Handler to call when the value changes.
   * Will be called in real-time if `mode` is `live` (default).
   */
  onChange?: (newValue: number | null) => unknown
  /**
   * The precision to round to.
   */
  precision?: number
  /**
   * Whether to emit changes in real-time as the inner string
   * value changes, or whether to let the consumer use `connector.commit()`
   * to do so.
   */
  mode?: 'live' | 'draft'
  /**
   * Locale to use for number formatting.
   */
  locale?: string
}

/**
 * Numeric input controller.
 */
export function useNumericInput(
  props: NumericInputHookConfig
): NumericConnector {
  // The actual textual input value.
  const [innerValue, setInputValue] = React.useState(
    formatValueForInput(props.value, props)
  )

  // When the value prop changes, if the change was triggered
  // by something other than an input change, set the inner value.
  React.useEffect(() => {
    setInputValue(formatValueForInput(props.value, props))
  }, [props.value])

  // Used for changing the string value.
  const changeValue = React.useCallback(
    (str: string, noFormat = false): string => {
      const value = formatValueForInput(str, props)
      setInputValue(noFormat ? String(str) : value)
      return String(str)
    },
    [props.precision, props.locale]
  )

  // Emits the numeric value to the consumer.
  const emitValue = React.useCallback(
    (value: number | null) => {
      if (props.onChange && value !== props.value) {
        if (value === null) {
          props.onChange(null)
        } else {
          props.onChange(toPrecision(value, props.precision))
        }
      }
    },
    [props.onChange, props.value, props.precision]
  )

  // Changes the value and emits it to the consumer.
  const changeValueAndEmit = React.useCallback(
    (str: string, noFormat = false): void => {
      const value = changeValue(str, noFormat)
      if (isEmptyValue(value)) {
        emitValue(null)
      } else {
        const converted = parseNumber(value, props)
        if (!Number.isNaN(converted)) {
          emitValue(converted)
        } else {
          emitValue(0)
        }
      }
    },
    [props.precision, props.locale, changeValue, emitValue]
  )

  // Whether the specified string is considered a valid
  // number text.
  const isValidNumericString = React.useCallback(
    (value: string) => {
      if (props.precision === 0) {
        return Array.from(value).every((ch) => isDigit(ch))
      }
      const separators = getSeparators(getLocale(props))
      return Array.from(value).every(
        (ch) =>
          isDigit(ch) ||
          ch === '-' ||
          ch === separators.thousand ||
          ch === separators.decimal
      )
    },
    [props.precision]
  )

  // Can be used by the consumer to prevent key presses
  // that contain invalid characters.
  const keyDownPreventNonNumeric = React.useCallback(
    (e: React.KeyboardEvent) => {
      // Allow some control keys to be used.
      switch (e.key) {
        case 'Enter':
        case 'Esc':
        case 'Backspace':
        case 'Tab':
        case 'Delete':
          return true
      }

      if (e.metaKey || e.ctrlKey) {
        return true
      }

      // Allow arrow keys
      if (e.key.startsWith('Arrow')) {
        return true
      }

      if (!isValidNumericString(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }

      return true
    },
    []
  )

  // When using draft mode, used for committing the value to the consumer.
  const commit = React.useCallback(() => {
    changeValueAndEmit(innerValue)
  }, [changeValueAndEmit, innerValue])

  // The consumer can call this with a string to
  // change the inner textual value. Will be returned as `value`
  // on the connector.
  const onInnerValueChange = React.useCallback(
    (value: string) => {
      if (props.mode === 'draft') {
        setInputValue(value)
      } else {
        changeValueAndEmit(value, true)
      }
    },
    [props.mode, changeValueAndEmit]
  )

  // The connector.
  return React.useMemo<NumericConnector>(
    () => ({
      changeValue,
      changeValueAndEmit,
      commit,
      isValidNumericString,
      keyDownPreventNonNumeric,
      value: innerValue,
      onChange: onInnerValueChange,
    }),
    [
      changeValue,
      changeValueAndEmit,
      isValidNumericString,
      keyDownPreventNonNumeric,
      onInnerValueChange,
      commit,
    ]
  )
}

/**
 * The actions that can be triggered to interact with the
 * numeric input state.
 */
export interface NumericConnector {
  /**
   * Connect this to an `onChange` handler for the textual input.
   * @param value
   */
  onChange(value: string): unknown
  /**
   * The string-formatted number to render in the input field.
   */
  value: string
  /**
   * If `mode` is `draft`, call this when you want to emit a change.
   * If `mode` is `live`, then you shouldn't use this.
   */
  commit(): unknown
  /**
   * Changes the inner value but does not emit it.
   *
   * @param str the value to set
   * @param noFormat if `true`, does not format the value
   * @returns the formatted, fixed value
   */
  changeValue(str: string, noFormat?: boolean): string
  /**
   * Triggers a change to the value which will be emitted.
   *
   * @param {string} str the value to set
   * @param {boolean} noFormat if true, does not format the value
   */
  changeValueAndEmit(str: string, noFormat?: boolean): void
  /**
   * onKeyDown handler to prevent non-numeric characters from being entered into an input.
   */
  keyDownPreventNonNumeric(e: React.KeyboardEvent): unknown
  /**
   * Determines if the value consists solely of valid characters used
   * for composing a numeric string.
   *
   * @param {string} value
   */
  isValidNumericString(value: string): boolean
}

/**
 * Formats the value accordingly.
 *
 * @param {string} value the `Number` to format
 * @param {NumericInputHookConfig} props caller can specify the prop bag to look at
 */
function formatValueForInput(
  value: string | number | undefined | null,
  props: NumericInputHookConfig
) {
  if (isEmptyValue(value)) {
    return ''
  }

  const parsed = toPrecision(parseNumber(value, props), getSanePrecision(props))

  if (Number.isNaN(parsed)) {
    return ''
  }

  return parsed.toLocaleString(getLocale(props), {
    minimumFractionDigits: getSanePrecision(props),
  })
}

/**
 * Gets sanitized precision.
 */
function getSanePrecision(props: NumericInputHookConfig) {
  const prec = toPrecision(Number(props.precision), 0)
  return Number.isNaN(prec) ? 0 : Math.max(0, prec)
}

/**
 * Gets the locale from props.
 *
 * @param {*} props
 */
function getLocale(props: NumericInputHookConfig) {
  return props.locale || navigator.language
}

/**
 * Determines if the value is empty (0 numeric does not count as empty)
 *
 * @param {*} value
 */
function isEmptyValue(
  value: number | string | null | undefined
): value is null | undefined | '' {
  return value === null || value === undefined || value === ''
}

/**
 * Cache used for separator lookup.
 */
const separatorsCache = new Map<string, NumericSeparators>()

/**
 * `getLocaleNumericSeparators` with caching.
 *
 * @param {string} locale
 */
function getSeparators(locale: string) {
  if (!separatorsCache.has(locale)) {
    separatorsCache.set(locale, getLocaleNumericSeparators(locale))
  }

  return separatorsCache.get(locale)!
}

/**
 * Parses the number according to the specified props.
 *
 * @param {string} value
 * @param {NumericInputHookConfig} props
 */
function parseNumber(value: string | number, props: NumericInputHookConfig) {
  if (typeof value === 'number') {
    return value
  }
  const locale = getLocale(props)
  return parseNumberFromPartialString(value, getSeparators(locale))
}
